import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InsertCommentOutlinedIcon from '@mui/icons-material/InsertCommentOutlined';
import SensorsOutlinedIcon from '@mui/icons-material/SensorsOutlined';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {lighten} from '@mui/system/colorManipulator';
import type {FC} from 'react';
import {useTranslation} from 'react-i18next';
import DialogPanel from '@/components/common/DialogPanel';
import ValueLabel from '@/components/common/ValueLabelForPopUp';
import type {LidarTechnology} from '@/domain/entities/LidarTechnology';
import {SLAM_POINT_CLOUD_STATUS, type SlamPointCloud} from '@/domain/entities/SlamPointCloud';
import {primaryColor} from '@/theme/theme';

//TYPES
type Props = {
  lidarTechnologies?: Array<LidarTechnology>;
  slamPointCloud: SlamPointCloud;
  canDelete: boolean;
  canUpload: boolean;
  onClose: () => void;
  onDelete: (pointCludUuid: string) => void;
  onDownload: () => void;
  onReupload: () => void;
};

const SlamPointCloudInfo: FC<Props> = ({
  slamPointCloud,
  lidarTechnologies,
  canDelete,
  canUpload,
  onDelete,
  onClose,
  onDownload,
  onReupload,
}) => {
  const {t} = useTranslation();

  const {uuid, status, date, lidarTechnologyUuid, comments, createdAt} = slamPointCloud;

  const lidarTechnologyLabel = lidarTechnologies?.find((tech) => tech.uuid === lidarTechnologyUuid)?.name || '-';

  const statusLabels: Record<SLAM_POINT_CLOUD_STATUS, string> = {
    [SLAM_POINT_CLOUD_STATUS.DATA_PENDING]: t('pointClouds.DATA_PENDING'),
    [SLAM_POINT_CLOUD_STATUS.DATA_UPLOADED]: t('pointClouds.DATA_UPLOADED'),
  };

  return (
    <DialogPanel isOpen={true} label={`${t('pointClouds.detail')} SLAM`} onClose={onClose}>
      <Stack sx={{p: 2}}>
        <ValueLabel
          value={createdAt.format('YYYY-MM-DD')}
          icon={<CalendarTodayOutlinedIcon sx={{color: 'grey.700'}} />}
          label={t('pointClouds.createdAt')}
        />
        <Divider />
        <ValueLabel
          value={statusLabels[status]}
          icon={<InfoOutlinedIcon sx={{color: 'grey.700'}} />}
          label={t('pointClouds.status')}
        />
        <Divider />
        <ValueLabel
          value={date.format('YYYY-MM-DD')}
          icon={<CalendarMonthOutlinedIcon sx={{color: 'grey.700'}} />}
          label={t('pointClouds.date')}
        />
        <Divider />
        <ValueLabel
          value={lidarTechnologyLabel}
          icon={<SensorsOutlinedIcon sx={{color: 'grey.700'}} />}
          label={t('pointClouds.lidarTechnology')}
        />
        <Divider />
        <ValueLabel
          direction="column"
          value={comments || '-'}
          icon={<InsertCommentOutlinedIcon sx={{color: 'grey.700'}} />}
          label={t('pointClouds.comments')}
        />
      </Stack>

      {status === SLAM_POINT_CLOUD_STATUS.DATA_UPLOADED ? (
        <Stack direction="row" sx={{gap: 2, mt: 2, justifyContent: 'flex-end'}}>
          {canDelete && (
            <Button
              startIcon={<DeleteOutlineOutlinedIcon />}
              variant="outlined"
              sx={{color: 'primary.main', mt: 1}}
              onClick={() => onDelete(uuid)}
            >
              {t('delete')}
            </Button>
          )}
          <Button
            startIcon={<FileDownloadOutlinedIcon />}
            variant="contained"
            sx={{bgcolor: lighten(primaryColor, 0.5), color: 'primary.main', mt: 1}}
            onClick={onDownload}
          >
            {t('download')}
          </Button>
        </Stack>
      ) : (
        <Stack sx={{mt: 2}}>
          <Typography sx={{color: 'grey.700'}}>{t('pointClouds.noDataUploaded')}</Typography>
          <Stack direction="row" sx={{gap: 2, justifyContent: 'flex-end'}}>
            {canDelete && (
              <Button
                startIcon={<DeleteOutlineOutlinedIcon />}
                variant="outlined"
                sx={{color: 'primary.main', mt: 1}}
                onClick={() => onDelete(uuid)}
              >
                {t('delete')}
              </Button>
            )}
            {canUpload && (
              <Button
                startIcon={<FileUploadOutlinedIcon />}
                variant="contained"
                sx={{bgcolor: lighten(primaryColor, 0.5), color: 'primary.main', mt: 1}}
                onClick={onReupload}
              >
                {t('pointClouds.reupload')}
              </Button>
            )}
          </Stack>
        </Stack>
      )}
    </DialogPanel>
  );
};
export default SlamPointCloudInfo;
